import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Sprachausgabe via Web Speech API (SpeechSynthesis).
 * - Kostenlos, offline, funktioniert in allen modernen Browsern.
 * - Bevorzugt eine deutsche Stimme (de-DE / de-*).
 */
export function useSpeechSynthesis({ language = "de-DE", rate = 1.0, pitch = 1.0 } = {}) {
  const [speaking, setSpeaking] = useState(false);
  const [supported] = useState(
    typeof window !== "undefined" && !!window.speechSynthesis && !!window.SpeechSynthesisUtterance,
  );
  const voiceRef = useRef(null);

  useEffect(() => {
    if (!supported) return;
    const pickVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices?.length) return;
      // Prefer exact match, dann startsWith de, dann irgendeine
      voiceRef.current =
        voices.find(v => v.lang === language) ||
        voices.find(v => v.lang?.startsWith(language.split("-")[0])) ||
        voices[0];
    };
    pickVoice();
    window.speechSynthesis.onvoiceschanged = pickVoice;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, [language, supported]);

  const speak = useCallback((text) => {
    if (!supported || !text) return;
    try {
      window.speechSynthesis.cancel(); // stoppe Vorheriges
      const u = new window.SpeechSynthesisUtterance(String(text));
      u.lang = language;
      u.rate = rate;
      u.pitch = pitch;
      if (voiceRef.current) u.voice = voiceRef.current;
      u.onstart = () => setSpeaking(true);
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
    } catch {
      setSpeaking(false);
    }
  }, [supported, language, rate, pitch]);

  const stop = useCallback(() => {
    if (!supported) return;
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    setSpeaking(false);
  }, [supported]);

  useEffect(() => () => { if (supported) { try { window.speechSynthesis.cancel(); } catch { /* noop */ } } }, [supported]);

  return { supported, speaking, speak, stop };
}
